import { AdminPermissions } from '@order-management/roles';
import {
  AdminDevicesCriteria,
  AdminDevicesFilterCriteria,
  DeferredResponse,
  InputType,
  PaginatedAPIResponse,
  SalesOrder,
  SortDirection,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
} from 'react-router-dom';
import { AdminProvider } from '../../shared/context/admin-context';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import AdminHeader from '../admin-header/admin-header';
import AdminInboxTable, {
  AdminToolsView,
} from '../admin-inbox-table/admin-inbox-table';
import { fetchDevicesSalesOrders as fetchDevicesSalesOrdersRequest } from '../admin-service';
import { adminDevicesTableColumns } from '../admin-table/admin-table-columns';

const initialCriteria: AdminDevicesCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

const FETCH_ADMIN_DEVICES_SALES_ORDERS_ERROR_MESSAGE =
  'Devices sales orders could not be fetched. Please try again';
const FETCH_ADMIN_DEVICES_BY_SALES_ORDER_ERROR_MESSAGE =
  'Devices could not be fetched. Please try again';
const DEFAULT_INBOX_TABLE_TEXT =
  'Please select a sales order to display devices';
const TABLE_EMPTY_TEXT = 'There are no devices at the moment';
const INBOX_EMPTY_TEXT = 'There are no sales orders at the moment';

const initialFilterCriteria: AdminDevicesFilterCriteria = {
  salesOrders: [],
  serialNumbers: [],
};

export const loader: LoaderFunction = () => {
  const response = fetchDevicesSalesOrdersRequest(initialCriteria);

  return defer({ response });
};

export function AdminDevicesPageContent() {
  const devicesSalesOrdersInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<SalesOrder>;
  const [devicesSalesOrders, setDevicesSalesOrders] = useState<SalesOrder[]>(
    devicesSalesOrdersInitialResponse.data
  );
  const [devicesSalesOrdersDataResponse, setDevicesSalesOrdersDataResponse] =
    useState<PaginatedAPIResponse<SalesOrder>>(
      devicesSalesOrdersInitialResponse
    );
  const [salesOrderCriteria, setSalesOrderCriteria] =
    useState<AdminDevicesCriteria>(initialCriteria);
  const [filterCriteria, setFilterCriteria] =
    useState<AdminDevicesFilterCriteria>(initialFilterCriteria);
  const {
    executeHttpRequest: executeFetchDevicesSalesOrdersRequest,
    isLoading: loadingInitialSalesOrders,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchMoreDevicesSalesOrdersRequest } =
    useHttpRequest();

  const hasMoreDevicesSalesOrders = useMemo(
    () => !!devicesSalesOrdersDataResponse.hasMore,
    [devicesSalesOrdersDataResponse]
  );

  const fetchDeviceSalesOrders = useCallback(
    async (criteria: AdminDevicesCriteria) =>
      executeFetchDevicesSalesOrdersRequest({
        asyncFunction: async () => {
          const response = await fetchDevicesSalesOrdersRequest(criteria);
          setDevicesSalesOrders(response.data);
          setDevicesSalesOrdersDataResponse(response);
        },
        customErrorMessage: FETCH_ADMIN_DEVICES_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeFetchDevicesSalesOrdersRequest]
  );

  const fetchMoreDevicesSalesOrders = useCallback(
    () =>
      executeFetchMoreDevicesSalesOrdersRequest({
        asyncFunction: async () => {
          const nextPage = salesOrderCriteria.pageNumber + 1;
          const updatedCriteria = {
            ...salesOrderCriteria,
            pageNumber: nextPage,
          };
          const response = await fetchDevicesSalesOrdersRequest(
            updatedCriteria
          );
          setSalesOrderCriteria(updatedCriteria);
          setDevicesSalesOrders((prevDevicesSalesOrders) =>
            prevDevicesSalesOrders.concat(response.data)
          );
          setDevicesSalesOrdersDataResponse(response);
        },
        customErrorMessage: FETCH_ADMIN_DEVICES_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [salesOrderCriteria, executeFetchMoreDevicesSalesOrdersRequest]
  );

  const handleTableFilterSubmit = useCallback(
    async (updatedDevicesSalesOrdercriteria: AdminDevicesCriteria) => {
      await fetchDeviceSalesOrders(updatedDevicesSalesOrdercriteria);
    },
    [fetchDeviceSalesOrders]
  );

  const onSearchChange = useCallback(
    (search: string) => {
      const updatedCriteria = {
        ...initialCriteria,
        ...filterCriteria,
        search,
      };
      setSalesOrderCriteria(updatedCriteria);
      handleTableFilterSubmit(updatedCriteria);
    },
    [filterCriteria, handleTableFilterSubmit]
  );

  const filtersData = useMemo(
    () =>
      ({
        salesOrders: {
          title: 'Sales order',
          appliedCount: filterCriteria.salesOrders?.length,
          value: filterCriteria.salesOrders,
          input: {
            type: InputType.FILTER_CRITERIA,
            filterValueKey: 'salesOrders',
            valueKey: 'salesOrders',
            placeholder: 'Search by SO',
          },
        },
        serialNumbers: {
          title: 'Serial number',
          appliedCount: filterCriteria.serialNumbers?.length,
          value: filterCriteria.serialNumbers,
          input: {
            type: InputType.FILTER_CRITERIA,
            filterValueKey: 'serialNumbers',
            valueKey: 'serialNumbers',
            placeholder: 'Search by Serial number',
          },
        },
      } as FiltersData<AdminDevicesFilterCriteria>),
    [filterCriteria.salesOrders, filterCriteria.serialNumbers]
  );

  const onFilterSubmit = useCallback(
    (values: AdminDevicesFilterCriteria) => {
      const updatedFilterCriteria = {
        salesOrders: values.salesOrders,
        serialNumbers: values.serialNumbers,
      };
      const updatedCriteria = {
        ...initialCriteria,
        ...updatedFilterCriteria,
        search: salesOrderCriteria.search,
      };
      setFilterCriteria(updatedFilterCriteria);
      setSalesOrderCriteria(updatedCriteria);
      handleTableFilterSubmit(updatedCriteria);
    },
    [handleTableFilterSubmit, salesOrderCriteria.search]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof AdminDevicesFilterCriteria)[]) => {
      const updatedFilterCriteria = { ...filterCriteria };
      for (const filterDataKey of filterDataKeys) {
        updatedFilterCriteria[filterDataKey] =
          initialFilterCriteria[filterDataKey];
      }
      onFilterSubmit(updatedFilterCriteria);
    },
    [filterCriteria, onFilterSubmit]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4 mb-4"
      header={
        <AdminHeader
          title="Devices"
          search={salesOrderCriteria.search}
          onSearchChange={onSearchChange}
        />
      }
    >
      <Element display="flex" className="w-full mb-2">
        <FilterBar
          initialData={initialFilterCriteria}
          data={filterCriteria}
          filtersData={filtersData}
          onFilterSubmit={onFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />
      </Element>
      <AdminInboxTable
        fetchMoreInboxItems={fetchMoreDevicesSalesOrders}
        hasMoreInboxItems={hasMoreDevicesSalesOrders}
        inboxItems={devicesSalesOrders}
        loadingInitialInboxItems={loadingInitialSalesOrders}
        tableColumns={adminDevicesTableColumns}
        searchValue={salesOrderCriteria.search}
        filterCriteria={filterCriteria}
      />
    </BoxLayout>
  );
}

export function AdminDevicesPage() {
  const { response } = useLoaderData() as DeferredResponse<
    Promise<PaginatedAPIResponse<SalesOrder>>
  >;

  return (
    <ProtectedPage permissions={AdminPermissions.ADMIN_TOOLS_ACCESS}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <AdminProvider
            adminView={AdminToolsView.DEVICES}
            fetchTableRecordsErrorMessage={
              FETCH_ADMIN_DEVICES_BY_SALES_ORDER_ERROR_MESSAGE
            }
            defaultInboxTableText={DEFAULT_INBOX_TABLE_TEXT}
            tableEmptyText={TABLE_EMPTY_TEXT}
            inboxEmptyText={INBOX_EMPTY_TEXT}
          >
            <AdminDevicesPageContent />
          </AdminProvider>
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default AdminDevicesPage;
